import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { Loader2, FileText, ArrowLeft, Copy } from "lucide-react";
import { generateFootnotes } from "@/lib/api/ai-writing.functions";
import { CITATION_STYLES } from "@/lib/citation-styles";

export const Route = createFileRoute("/education/research/footnotes")({
  head: () => ({
    meta: [
      { title: "Footnote Generator — Legal Research Hub | Nkadimeng Danny" },
      {
        name: "description",
        content:
          "Paste a passage of legal writing; we'll insert numbered footnote markers and produce a matching footnote list in OSCOLA, Harvard, APA 7 or SA citation.",
      },
      { property: "og:title", content: "Footnote Generator" },
    ],
  }),
  component: FootnotePage,
});

function FootnotePage() {
  const run = useServerFn(generateFootnotes);
  const [style, setStyle] = useState<"oscola" | "harvard" | "apa-7" | "sa-citation">("sa-citation");
  const [passage, setPassage] = useState("");
  const [loading, setLoading] = useState(false);
  const [out, setOut] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [balance, setBalance] = useState<number | null>(null);

  async function onRun(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    setErr(null);
    setOut(null);
    try {
      const r = await run({ data: { style, passage } });
      setBalance(r.balance);
      if (r.ok) setOut(r.output);
      else setErr(r.error);
    } catch (e: any) {
      const msg = String(e?.message ?? "");
      if (msg.includes("INSUFFICIENT_CREDITS"))
        setErr("You've run out of AI credits. Buy more from your portal credits page.");
      else if (msg.includes("RATE_LIMIT"))
        setErr("Too many requests right now — try again in a minute.");
      else setErr("AI service is unavailable. Try again shortly.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="container-narrow py-10">
      <Link to="/education/research" className="inline-flex items-center gap-1 text-xs text-navy/70 hover:text-navy">
        <ArrowLeft className="h-3.5 w-3.5" /> Back to Research Hub
      </Link>
      <div className="mt-3 flex items-center gap-3">
        <span className="rounded-md bg-navy/5 p-2 text-navy">
          <FileText className="h-5 w-5" />
        </span>
        <div>
          <h1 className="font-display text-3xl text-navy">Footnote Generator</h1>
          <p className="text-sm text-muted-foreground">1 credit per generation</p>
        </div>
      </div>

      <form onSubmit={onRun} className="mt-6 space-y-4">
        <div>
          <label className="text-sm font-semibold text-navy">Citation style</label>
          <select
            value={style}
            onChange={(e) => setStyle(e.target.value as any)}
            className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
          >
            {CITATION_STYLES.map((s) => (
              <option key={s.slug} value={s.slug}>
                {s.shortName}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="text-sm font-semibold text-navy">Your draft passage</label>
          <textarea
            value={passage}
            onChange={(e) => setPassage(e.target.value)}
            required
            minLength={20}
            maxLength={6000}
            rows={10}
            placeholder="Paste a paragraph (or several) of your draft. We'll insert numbered footnote markers and list the footnotes underneath."
            className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm font-mono"
          />
          <p className="mt-1 text-xs text-muted-foreground">{passage.length}/6000</p>
        </div>
        <button
          type="submit"
          disabled={loading}
          className="inline-flex items-center gap-2 rounded-md bg-navy px-5 py-2.5 text-sm font-semibold text-navy-foreground disabled:opacity-60"
        >
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4" />}
          Generate footnotes (1 credit)
        </button>
      </form>

      {balance !== null && (
        <p className="mt-3 text-xs text-muted-foreground">
          Credits remaining: <strong>{balance}</strong>
        </p>
      )}
      {err && <p className="mt-4 rounded-md bg-rose-50 p-3 text-sm text-rose-800">{err}</p>}

      {out && (
        <article className="mt-6 rounded-lg border border-input bg-card p-5">
          <div className="mb-3 flex justify-end">
            <button
              onClick={() => navigator.clipboard.writeText(out)}
              className="inline-flex items-center gap-1 rounded-md border border-input px-3 py-1.5 text-xs hover:bg-muted"
            >
              <Copy className="h-3.5 w-3.5" /> Copy
            </button>
          </div>
          <pre className="whitespace-pre-wrap font-sans text-sm leading-relaxed text-foreground">{out}</pre>
          <p className="mt-4 border-t border-border pt-3 text-xs italic text-muted-foreground">
            AI-generated. Verify every citation against the primary source before relying on it.
          </p>
        </article>
      )}
    </div>
  );
}
